import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Arrow, Communication, General } from 'icons/untitledui';
import {
  DropdownMenu,
  type DropdownMenuTriggerRenderProps,
} from 'shared/components/dropdown/DropdownMenu';
import {
  buildComposeUrl,
  EMAIL_PROVIDERS,
  type EmailComposePayload,
  PROVIDER_LABEL_KEYS,
} from 'shared/components/EmailComposeMenu/_index';

import { usePosthogCapture } from 'events/posthogCapture/_index';
import * as Styled from 'events/styles/DaySummary.style';
import * as Text from 'theme/components/Text.style';

import ArchChart from './ArchChart';
import EditPreferencesView from './EditPreferencesView';
import Sparkline from './Sparkline';

export interface DepartmentOption {
  id: string;
  label: string;
}

export interface DaySummaryProps {
  title: string;
  selectedDepartment: DepartmentOption;
  departments?: DepartmentOption[];
  resolvedCount: number;
  openCount: number;
  summary?: React.ReactNode;
  expanded?: boolean;
  defaultExpanded?: boolean;
  collapsible?: boolean;
  editingPreferences?: boolean;
  initialPreferences?: string[];
  isSavingPreferences?: boolean;
  onToggleExpanded?: (expanded: boolean) => void;
  onDepartmentChange?: (option: DepartmentOption) => void;
  shareEmail?: EmailComposePayload;
  onEditPreferences?: () => void;
  onSavePreferences?: (preferences: string[]) => void;
  onFeedback?: () => void;
}

function DepartmentSelector({
  selectedDepartment,
  departments,
  onDepartmentChange,
}: {
  selectedDepartment: DepartmentOption;
  departments?: DepartmentOption[];
  onDepartmentChange?: (option: DepartmentOption) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const capture = usePosthogCapture();

  const items = useMemo(
    () =>
      (departments ?? []).map((dept) => ({
        label: <Text.P2>{dept.label}</Text.P2>,
        selected: dept.id === selectedDepartment.id,
        onSelect: ({ close }: { close: () => void }) => {
          if (dept.id !== selectedDepartment.id) {
            capture.daySummaryDepartmentChanged({ department_id: dept.id });
          }
          onDepartmentChange?.(dept);
          close();
        },
      })),
    [departments, selectedDepartment.id, onDepartmentChange, capture]
  );

  return (
    <Styled.DepartmentMenuWrapper>
      <DropdownMenu
        isOpen={open}
        onOpenChange={setOpen}
        items={items}
        usePortal
        renderTrigger={({
          toggle,
          triggerRef,
        }: DropdownMenuTriggerRenderProps) => (
          <Styled.DepartmentTrigger
            ref={triggerRef}
            type="button"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              toggle(e);
            }}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <span>{selectedDepartment.label}</span>
            <Arrow.ChevronSelectorVertical />
          </Styled.DepartmentTrigger>
        )}
      />
    </Styled.DepartmentMenuWrapper>
  );
}

export default function DaySummary({
  title,
  selectedDepartment,
  departments,
  resolvedCount,
  openCount,
  summary,
  expanded,
  defaultExpanded = false,
  collapsible = true,
  editingPreferences = false,
  initialPreferences = [],
  isSavingPreferences = false,
  onToggleExpanded,
  onDepartmentChange,
  shareEmail,
  onEditPreferences,
  onSavePreferences,
  onFeedback,
}: DaySummaryProps): JSX.Element {
  const { t } = useTranslation('pageEvents');
  const { t: tShared } = useTranslation('shared');
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showingShareProviders, setShowingShareProviders] = useState(false);
  const capture = usePosthogCapture();

  useEffect(() => {
    if (!menuOpen) setShowingShareProviders(false);
  }, [menuOpen]);

  const isExpanded = expanded ?? internalExpanded;
  const isEmpty = resolvedCount + openCount === 0;

  const setExpanded = (next: boolean) => {
    if (next) {
      capture.daySummaryExpanded();
    } else {
      capture.daySummaryCollapsed();
    }
    if (expanded === undefined) setInternalExpanded(next);
    onToggleExpanded?.(next);
  };

  const shareProviderItems = useMemo(
    () =>
      EMAIL_PROVIDERS.map((provider) => ({
        label: <Text.P2>{tShared(PROVIDER_LABEL_KEYS[provider])}</Text.P2>,
        icon: <Arrow.ArrowUpRight size={20} />,
        onSelect: ({ close }: { close: () => void }) => {
          if (!shareEmail) return;
          window.open(
            buildComposeUrl(provider, shareEmail),
            '_blank',
            'noopener,noreferrer'
          );
          capture.daySummaryEmailSent({ provider });
          close();
        },
      })),
    [shareEmail, tShared, capture]
  );

  const mainMenuItems = useMemo(
    () => [
      ...(shareEmail
        ? [
            {
              label: <Text.P2>{t('daySummary.menu.share')}</Text.P2>,
              icon: <Communication.Send01 size={20} />,
              onSelect: () => {
                capture.daySummaryMenuShareClicked();
                setShowingShareProviders(true);
              },
            },
          ]
        : []),
      {
        label: <Text.P2>{t('daySummary.menu.editPreferences')}</Text.P2>,
        icon: <General.Settings04 size={20} />,
        onSelect: ({ close }: { close: () => void }) => {
          capture.daySummaryMenuEditPreferencesClicked();
          onEditPreferences?.();
          close();
        },
      },
      {
        label: <Text.P2>{t('daySummary.menu.feedback')}</Text.P2>,
        icon: <Communication.MessageTextSquare02 size={20} />,
        onSelect: ({ close }: { close: () => void }) => {
          capture.daySummaryMenuFeedbackClicked();
          onFeedback?.();
          close();
        },
      },
    ],
    [t, shareEmail, onEditPreferences, onFeedback, capture]
  );

  const menuItems = showingShareProviders ? shareProviderItems : mainMenuItems;
  const menuHeaderLabel = showingShareProviders
    ? tShared('emailCompose.shareWith')
    : undefined;

  const header = (
    <Styled.Header>
      <Styled.HeaderTitleColumn>
        <Styled.Title>{title}</Styled.Title>
        <DepartmentSelector
          selectedDepartment={selectedDepartment}
          departments={departments}
          onDepartmentChange={onDepartmentChange}
        />
      </Styled.HeaderTitleColumn>
      <Styled.HeaderLegendColumn>
        <Styled.LegendInline>
          <span>
            {t('daySummary.legend.resolved', { count: resolvedCount })}
          </span>
          <Styled.LegendIcon $colour={Styled.RESOLVED_COLOUR}>
            <General.CheckCircle />
          </Styled.LegendIcon>
        </Styled.LegendInline>
        <Styled.LegendInline>
          <span>{t('daySummary.legend.open', { count: openCount })}</span>
          <Styled.LegendIcon $colour={Styled.OPEN_COLOUR}>
            <General.Circle />
          </Styled.LegendIcon>
        </Styled.LegendInline>
      </Styled.HeaderLegendColumn>
    </Styled.Header>
  );

  if (editingPreferences) {
    return (
      <Styled.Card key="edit-preferences" aria-label={title}>
        <EditPreferencesView
          initialPreferences={initialPreferences}
          isSaving={isSavingPreferences}
          onSave={(preferences) => onSavePreferences?.(preferences)}
        />
      </Styled.Card>
    );
  }

  if (!isExpanded) {
    const handleKey = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setExpanded(true);
      }
    };
    return (
      <Styled.Card
        key="collapsed"
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(true)}
        onKeyDown={handleKey}
        aria-expanded={false}
        aria-label={t('daySummary.expandLabel', { title })}
      >
        {header}
        <Sparkline resolvedCount={resolvedCount} openCount={openCount} />
      </Styled.Card>
    );
  }

  return (
    <Styled.Card key="expanded" aria-expanded>
      {header}
      <Styled.ChartBlock>
        <ArchChart resolvedCount={resolvedCount} openCount={openCount} />
      </Styled.ChartBlock>
      {!isEmpty && summary && (
        <Styled.SummaryText>{summary}</Styled.SummaryText>
      )}
      <Styled.BottomActions>
        <DropdownMenu
          isOpen={menuOpen}
          onOpenChange={setMenuOpen}
          items={menuItems}
          headerLabel={menuHeaderLabel}
          usePortal
          renderTrigger={({
            toggle,
            triggerRef,
          }: DropdownMenuTriggerRenderProps) => (
            <Styled.MenuButton
              ref={triggerRef}
              type="button"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                toggle(e);
              }}
              aria-label={t('daySummary.menu.label')}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <General.DotsHorizontal />
            </Styled.MenuButton>
          )}
        />
        {collapsible && (
          <Styled.CollapseButton
            type="button"
            onClick={() => setExpanded(false)}
          >
            {t('daySummary.collapse')}
          </Styled.CollapseButton>
        )}
      </Styled.BottomActions>
    </Styled.Card>
  );
}
